import * as Sentry from '@sentry/vue';
import { AxiosInstance } from 'axios';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { ENV_VALUES } from '@/config/env';

const SENTRY_ERROR_CODES = [500, 501, 502, 503, 504, 505];
const SENTRY_ALL_ERRORS = false;

export const SENTRY_ENABLED = ENV_VALUES.VUE_APP_SENTRY_ENABLED;

// Initialize Sentry with router passed as parameter to avoid dependency cycle
// Router is passed from main.ts after it's created, breaking the cycle:
// router -> components -> BundurilRepo -> AwsAxiosConfig -> sentry -> router
export const initSentry = (router: InstanceType<typeof VueRouter>) => {
  if (!SENTRY_ENABLED) return;

  Sentry.init({
    enabled: SENTRY_ENABLED,
    Vue,
    dsn: ENV_VALUES.VUE_APP_SENTRY_DSN,
    // Disable default PII collection
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Reduce sampling to minimize data collection
    tracesSampleRate: 0.1,
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.informme\.info/],
    // Add beforeSend to scrub any remaining PII
    beforeSend(event) {
      // Set IP address to auto for geolocation without storing actual IP
      if (!event.user) {
        event.user = {};
      }
      event.user.ip_address = '{{auto}}';

      // Remove other PII
      if (event.user.email) delete event.user.email;
      if (event.user.username) delete event.user.username;

      return event;
    },

    // Logs
    enableLogs: ENV_VALUES.VUE_APP_SENTRY_LOGS_ENABLED,
  });
};

// Sentry.logger.info('User triggered test log', { log_source: 'sentry_test' });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function shouldCaptureAxiosError(error: any) {
  if (!error.response) {
    // Network errors, timeouts, etc.
    return true;
  }

  const { status } = error.response;

  if (SENTRY_ALL_ERRORS) {
    return status >= 400;
  }

  return SENTRY_ERROR_CODES.includes(status);
}

export const injectSentryAxiosInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const shouldCapture = shouldCaptureAxiosError(error);

      if (shouldCapture) {
        // Enrich error with additional context
        Sentry.withScope((scope) => {
          scope.setTag('errorSource', 'axios');
          scope.setContext('httpError', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            method: error.config?.method,
            responseData: error.response?.data,
          });

          Sentry.captureException(error);
        });
      }

      return Promise.reject(error);
    },
  );
};
